<template>
    <AppLayout>
        <div class="container p-4 mx-auto md:px-1 md:py-4">
            <!-- breadcrumb -->
            <nav
                class="mb-6 text-sm font-semibold"
                aria-label="Breadcrumb"
            >
                <ol class="inline-flex p-0 list-none">
                    <li class="flex items-center text-blue-500">
                        <inertia-link
                            :href="route('landing')"
                            class="text-gray-600"
                        >
                            Home
                        </inertia-link>
                        <svg
                            fill="#34D399"
                            class="w-3 h-3 mx-3 "
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 320 512"
                        >
                            <path
                                d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z"
                            />
                        </svg>
                    </li>
                    <li class="flex items-center">
                        <a
                            href="#"
                            class="text-gray-600"
                        >Assigned Retailers</a>
                    </li>
                </ol>
            </nav>
            <!-- breadcrumb end -->

            <div class="flex items-center justify-between my-6">
                <div class="text-xl font-bold">
                    Retailer Management
                </div>
            </div>

            <div>
                <t-input
                    v-model="search"
                    class="w-60"
                    placeholder="Search Retailers by name"
                    @keypress.enter="fetchPageData()"
                />

                <t-table
                    :responsive="true"
                    :responsive-breakpoint="640"
                    :headers="['S.N', 'Name', 'Email', 'User Since', 'Status', 'Actions']"
                    :data="users.data"
                    class="my-8 text-xs md:text-base"
                >
                    <template
                        slot="tbody"
                        slot-scope="{ tbodyClass, trClass, tdClass, renderResponsive, data, headers }"
                    >
                        <template v-if="renderResponsive">
                            <tbody
                                v-for="(row, rowIndex) in data"
                                :key="rowIndex"
                                :class="[tbodyClass, rowIndex % 2 === 0 ? 'bg-gray-100' : '']"
                            >
                                <tr
                                    v-for="header in headers"
                                    :key="header.text"
                                    :class="trClass"
                                >
                                    <th class="pl-1 text-sm font-semibold text-left text-gray-600 uppercase">
                                        {{ header.text }}
                                    </th>

                                    <td :class="[tdClass, 'relative']">
                                        <div v-if="header.text === 'S.N'">
                                            {{ rowIndex + 1 + (currentPage - 1) * perPage }}
                                        </div>

                                        <div v-if="header.text === 'Name'">
                                            {{ row.name }}
                                        </div>

                                        <div v-if="header.text === 'Email'">
                                            {{ (row.email && row.email.name) || 'No Email' }}
                                        </div>

                                        <div v-if="header.text === 'User Since'">
                                            {{ row.created_at | fromNow }}
                                        </div>

                                        <div v-if="header.text === 'Status'">
                                            {{ row.deleted_at ? 'Suspended' : 'Active' }}
                                        </div>

                                        <div v-if="header.text === 'Actions'">
                                            <div class="flex">
                                                <t-button :href="route('retailer.statement', {retailerId:row.id})">
                                                    Statement
                                                </t-button>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </template>
                    </template>

                    <template
                        slot="row"
                        slot-scope="{ trClass, tdClass, row, rowIndex }"
                    >
                        <tr :class="[trClass, 'text-xs md:text-base']">
                            <td :class="[tdClass]">
                                {{ rowIndex + 1 + (currentPage - 1) * perPage }}
                            </td>
                            <td :class="[tdClass]">
                                {{ row.name }}
                            </td>
                            <td :class="[tdClass]">
                                {{ row.email }}
                            </td>
                            <td :class="[tdClass]">
                                {{ row.created_at | fromNow }}
                            </td>
                            <td :class="[tdClass]">
                                {{ row.deleted_at ? 'Suspended' : 'Active' }}
                            </td>
                            <td :class="[tdClass]">
                                <div class="flex">
                                    <t-button :href="route('retailer.statement', {retailerId:row.id})">
                                        Statement
                                    </t-button>
                                </div>
                            </td>
                        </tr>
                    </template>
                </t-table>

                <t-pagination
                    v-if="users.data.length"
                    v-model="currentPage"
                    :total-items="totalRows"
                    :per-page="perPage"
                    :limit="limit"
                    :disabled="!users.data.length"
                    @change="fetchPageData"
                />
            </div>
        </div>
    </AppLayout>
</template>

<script>
import _ from 'lodash';
import AppLayout from '../../Layouts/AppLayout.vue';

export default {
    name: 'AssignedRetailerUser',

    components: { AppLayout },

    props: {
        users: {
            type: Object,
            required: true,
        },
        appliedFilters: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            currentPage: null,
            limit: 5,
            perPage: this.users.per_page,
            totalRows: this.users.total,
            search: null,
            mounted: false,
        };
    },

    watch: {
        search() {
            if (this.mounted) {
                this.debouncedFetchPageData();
            }
        },
    },

    created() {
        this.currentPage = this.users.current_page;
    },

    mounted() {
        if (this.appliedFilters.keyword) {
            this.search = this.appliedFilters.keyword;
        }

        this.debouncedFetchPageData = _.debounce(this.fetchPageData, 2000);

        this.$nextTick(() => {
            this.mounted = true;
        });
    },

    methods: {
        fetchPageData(newPage) {
            this.$inertia.get(
                route('users.management.retailer.assigned', {
                    page: newPage,
                    keyword: this.search,
                })
            );
        },
    },
};
</script>
